'use client'

import { MoreVert } from '@mui/icons-material'
import { IconButton, Menu, MenuItem } from '@mui/material'
import { useTranslations } from 'next-intl'
import { type MouseEvent, useState } from 'react'

import { useSnackbarControls } from '~/store/slices/display'

import useMarkAllNotificationsRead from '../useMarkAllNotificationsRead'

type Props = {
  unreadCount: number
}

/**
 * Overflow menu for bulk actions on the notifications surfaces, shared
 * by the bell dropdown and the notifications page. Marking everything read is
 * not undoable, so it sits behind a menu rather than a one-click button. A
 * snackbar reports how many rows cleared.
 */
export default function NotificationsActionsMenu({ unreadCount }: Props) {
  const t = useTranslations('notifications.common')
  const { openSnackbar } = useSnackbarControls()
  const { markAllRead, isLoading } = useMarkAllNotificationsRead()
  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const closeMenu = () => setAnchorEl(undefined)

  // The bell renders this inside its own open Menu; stopping propagation keeps
  // a click on these controls from reaching the dropdown behind them.
  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMarkAllRead = async (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    closeMenu()

    try {
      const count = await markAllRead()
      openSnackbar(t('markAllReadSuccess', { count }))
    } catch {
      openSnackbar(t('markAllReadError'), { severity: 'error' })
    }
  }

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('moreActions')}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={openMenu}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        // Menu composes its own Tab→close handler ahead of MenuListProps.onKeyDown,
        // so the reason guard — not the handler below — is what keeps Tab from
        // closing the menu. `reason` is widened because Menu emits 'tabKeyDown'
        // at runtime without declaring it.
        onClose={(event: object, reason: string) => {
          if (reason !== 'tabKeyDown') closeMenu()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Cycle focus through the items in both directions (AGENTS.md MenuList
        // Tab guidance for flat MenuItem children).
        MenuListProps={{
          onKeyDown: (e) => {
            if (e.key !== 'Tab') return
            e.preventDefault()
            const items = Array.from(
              e.currentTarget.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])')
            ) as HTMLElement[]
            const currentIndex = items.indexOf(document.activeElement as HTMLElement)
            const nextIndex = e.shiftKey
              ? currentIndex > 0
                ? currentIndex - 1
                : items.length - 1
              : currentIndex < items.length - 1
                ? currentIndex + 1
                : 0
            items[nextIndex]?.focus()
          }
        }}
        TransitionProps={{
          onEntered: (node) => {
            const first = node.querySelector('[role="menuitem"]') as HTMLElement | null
            first?.focus()
          }
        }}
      >
        <MenuItem disabled={unreadCount === 0 || isLoading} onClick={handleMarkAllRead}>
          {t('markAllRead')}
        </MenuItem>
      </Menu>
    </>
  )
}
